import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, DollarSign, ArrowRight } from "lucide-react";
import { BookingModal } from "./BookingModal";
import { ServiceImageSlideshow } from "./ServiceImageSlideshow";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useCurrency } from "@/hooks/use-currency";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";

import { LocalizedService, LocalizedContent, ServiceCategory } from "@/types";

// Type alias to match the component's expected interface
type Service = LocalizedService;

interface ServicesPreviewProps {
  services: Service[];
  categories?: ServiceCategory[];
}

export function ServicesPreview({ services, categories = [] }: ServicesPreviewProps) {
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}min`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  };

  const handleBookNow = (service: Service) => {
    setSelectedService(service);
    setIsBookingModalOpen(true);
  };

  // Helper to match category string (which is stored as English name) to category object
  const getCategoryName = (categoryString: string) => {
    if (!categories || !categories.length) return categoryString;

    const matchedCategory = categories.find(cat => {
      if (!cat || !cat.name) return false;

      const enName = typeof cat.name === 'string'
        ? cat.name
        : (cat.name as any).en || (cat.name as any).English || Object.values(cat.name)[0];

      return enName === categoryString;
    });

    return matchedCategory ? getLocalized(matchedCategory.name) : categoryString;
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            {t('services.title', 'Photography Services')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            {t('services.subtitle', 'Professional photography services tailored to capture your most precious moments')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.slice(0, 6).map((service) => (
            <Card key={service.id} className="group hover:shadow-[var(--shadow-elegant)] transition-all duration-300 overflow-hidden">
              <div className="relative h-64">
                <ServiceImageSlideshow
                  images={service.media_urls}
                  alt={getLocalized(service.name)}
                  className="h-full group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4 z-10">
                  <Badge variant="secondary" className="capitalize">
                    {getCategoryName(service.category)}
                  </Badge>
                </div>
              </div>

              <CardContent className="p-6">
                <h3 className="text-xl font-semibold mb-2">{getLocalized(service.name)}</h3>
                <p className="text-muted-foreground mb-4 line-clamp-2">
                  {getLocalized(service.description)}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {formatDuration(service.duration_minutes)}
                  </div>
                  <div className="flex items-center gap-2 font-semibold text-[hsl(var(--gold-dark))]">
                    <DollarSign className="h-4 w-4" />
                    {formatPrice(service.base_price)}
                  </div>
                </div>

                <Button
                  className="w-full group/btn bg-primary hover:bg-primary/90 transition-all duration-300"
                  onClick={() => handleBookNow(service)}
                >
                  {t('services.bookNow', 'Book Now')}
                  <ArrowRight className="h-4 w-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <Button
            variant="outline"
            size="lg"
            className="px-6 sm:px-8"
            asChild
          >
            <Link to="/services">{t('services.viewAll', 'View All Services')}</Link>
          </Button>
        </div>
      </div>

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        service={selectedService as any}
      />
    </section>
  );
}